'use client'

import React from 'react'
import type { ColumnDef, TanStackColumnDef } from './types'
import { COLUMN_CONFIG } from './config'
import { getBadgeColorByValue, getHeatmapColor, THEME_COLORS } from './theme/colors'

// Americas
import AR from 'country-flag-icons/react/3x2/AR'
import BO from 'country-flag-icons/react/3x2/BO'
import BR from 'country-flag-icons/react/3x2/BR'
import CA from 'country-flag-icons/react/3x2/CA'
import CL from 'country-flag-icons/react/3x2/CL'
import CO from 'country-flag-icons/react/3x2/CO'
import CR from 'country-flag-icons/react/3x2/CR'
import CU from 'country-flag-icons/react/3x2/CU'
import DO from 'country-flag-icons/react/3x2/DO'
import EC from 'country-flag-icons/react/3x2/EC'
import GT from 'country-flag-icons/react/3x2/GT'
import HN from 'country-flag-icons/react/3x2/HN'
import MX from 'country-flag-icons/react/3x2/MX'
import NI from 'country-flag-icons/react/3x2/NI'
import PA from 'country-flag-icons/react/3x2/PA'
import PE from 'country-flag-icons/react/3x2/PE'
import PR from 'country-flag-icons/react/3x2/PR'
import PY from 'country-flag-icons/react/3x2/PY'
import SV from 'country-flag-icons/react/3x2/SV'
import US from 'country-flag-icons/react/3x2/US'
import UY from 'country-flag-icons/react/3x2/UY'
import VE from 'country-flag-icons/react/3x2/VE'
// Europe
import AT from 'country-flag-icons/react/3x2/AT'
import BE from 'country-flag-icons/react/3x2/BE'
import CH from 'country-flag-icons/react/3x2/CH'
import CZ from 'country-flag-icons/react/3x2/CZ'
import DE from 'country-flag-icons/react/3x2/DE'
import DK from 'country-flag-icons/react/3x2/DK'
import ES from 'country-flag-icons/react/3x2/ES'
import FI from 'country-flag-icons/react/3x2/FI'
import FR from 'country-flag-icons/react/3x2/FR'
import GB from 'country-flag-icons/react/3x2/GB'
import GR from 'country-flag-icons/react/3x2/GR'
import HU from 'country-flag-icons/react/3x2/HU'
import IE from 'country-flag-icons/react/3x2/IE'
import IT from 'country-flag-icons/react/3x2/IT'
import NL from 'country-flag-icons/react/3x2/NL'
import NO from 'country-flag-icons/react/3x2/NO'
import PL from 'country-flag-icons/react/3x2/PL'
import PT from 'country-flag-icons/react/3x2/PT'
import RO from 'country-flag-icons/react/3x2/RO'
import RU from 'country-flag-icons/react/3x2/RU'
import SE from 'country-flag-icons/react/3x2/SE'
import UA from 'country-flag-icons/react/3x2/UA'
// Asia
import AE from 'country-flag-icons/react/3x2/AE'
import CN from 'country-flag-icons/react/3x2/CN'
import HK from 'country-flag-icons/react/3x2/HK'
import ID from 'country-flag-icons/react/3x2/ID'
import IL from 'country-flag-icons/react/3x2/IL'
import IN from 'country-flag-icons/react/3x2/IN'
import JP from 'country-flag-icons/react/3x2/JP'
import KR from 'country-flag-icons/react/3x2/KR'
import MY from 'country-flag-icons/react/3x2/MY'
import PH from 'country-flag-icons/react/3x2/PH'
import PK from 'country-flag-icons/react/3x2/PK'
import SA from 'country-flag-icons/react/3x2/SA'
import SG from 'country-flag-icons/react/3x2/SG'
import TH from 'country-flag-icons/react/3x2/TH'
import TR from 'country-flag-icons/react/3x2/TR'
import TW from 'country-flag-icons/react/3x2/TW'
import VN from 'country-flag-icons/react/3x2/VN'
// Africa & Oceania
import AU from 'country-flag-icons/react/3x2/AU'
import EG from 'country-flag-icons/react/3x2/EG'
import MA from 'country-flag-icons/react/3x2/MA'
import NG from 'country-flag-icons/react/3x2/NG'
import NZ from 'country-flag-icons/react/3x2/NZ'
import ZA from 'country-flag-icons/react/3x2/ZA'

const countryFlags: Record<string, React.ComponentType<any>> = {
  // Americas
  'Argentina': AR, 'Bolivia': BO, 'Brasil': BR, 'Canadá': CA, 'Chile': CL,
  'Colombia': CO, 'Costa Rica': CR, 'Cuba': CU, 'República Dominicana': DO,
  'Ecuador': EC, 'El Salvador': SV, 'Estados Unidos': US, 'Guatemala': GT,
  'Honduras': HN, 'México': MX, 'Nicaragua': NI, 'Panamá': PA, 'Paraguay': PY,
  'Perú': PE, 'Puerto Rico': PR, 'Uruguay': UY, 'Venezuela': VE,
  // Europe
  'Alemania': DE, 'Austria': AT, 'Bélgica': BE, 'Dinamarca': DK, 'España': ES,
  'Finlandia': FI, 'Francia': FR, 'Grecia': GR, 'Hungría': HU, 'Irlanda': IE,
  'Italia': IT, 'Noruega': NO, 'Países Bajos': NL, 'Polonia': PL, 'Portugal': PT,
  'Reino Unido': GB, 'República Checa': CZ, 'Rumania': RO, 'Rusia': RU,
  'Suecia': SE, 'Suiza': CH, 'Ucrania': UA,
  // Asia
  'Arabia Saudita': SA, 'China': CN, 'Corea del Sur': KR, 'Emiratos Árabes': AE,
  'Filipinas': PH, 'Hong Kong': HK, 'India': IN, 'Indonesia': ID, 'Israel': IL,
  'Japón': JP, 'Malasia': MY, 'Pakistán': PK, 'Singapur': SG, 'Tailandia': TH,
  'Taiwán': TW, 'Turquía': TR, 'Vietnam': VN,
  // Africa & Oceania
  'Australia': AU, 'Egipto': EG, 'Marruecos': MA, 'Nigeria': NG,
  'Nueva Zelanda': NZ, 'Sudáfrica': ZA,
}

export function getBadgeColors(value: string, isDarkMode = false): { bg: string; text: string } {
  return getBadgeColorByValue(value, isDarkMode)
}

function renderCountry(value: string): React.ReactNode {
  if (!value) return null
  const Flag = countryFlags[value]
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {Flag && <Flag style={{ width: 20, height: 14, borderRadius: 2 }} />}
      <span>{value}</span>
    </span>
  )
}

function renderBadge(value: string, isDarkMode: boolean, customColors?: Record<string, { bg: string; text: string }>): React.ReactNode {
  if (!value) return null
  const colors = customColors?.[value] || getBadgeColors(value, isDarkMode)
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 500,
      backgroundColor: colors.bg,
      color: colors.text,
      whiteSpace: 'nowrap',
    }}>
      {value}
    </span>
  )
}

function renderLink(value: string): React.ReactNode {
  if (!value) return null
  return (
    <a href={value} target="_blank" rel="noopener noreferrer" style={{
      color: THEME_COLORS.primary,
      textDecoration: 'none',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}>
      {value}
    </a>
  )
}

function renderEmail(value: string): React.ReactNode {
  if (!value) return null
  return (
    <a href={`mailto:${value}`} style={{
      color: THEME_COLORS.primary,
      textDecoration: 'none',
    }}>
      {value}
    </a>
  )
}

function renderPhone(value: string): React.ReactNode {
  if (!value) return null
  return (
    <a href={`tel:${value}`} style={{
      color: THEME_COLORS.primary,
      textDecoration: 'none',
    }}>
      {value}
    </a>
  )
}

function renderBoolean(value: boolean | string): React.ReactNode {
  const isTrue = value === true || value === 'true' || value === '1' || value === 'yes'
  return (
    <span style={{
      display: 'inline-block',
      width: 16,
      height: 16,
      borderRadius: 4,
      backgroundColor: isTrue ? THEME_COLORS.success : THEME_COLORS.error,
      opacity: 0.8,
    }} />
  )
}

function renderRating(value: number, max = 5): React.ReactNode {
  const stars = []
  for (let i = 0; i < max; i++) {
    stars.push(
      <span key={i} style={{ color: i < value ? '#F59E0B' : '#D1D5DB', fontSize: 14 }}>★</span>
    )
  }
  return <span style={{ display: 'inline-flex' }}>{stars}</span>
}

function renderProgress(value: number): React.ReactNode {
  const percent = Math.max(0, Math.min(100, value))
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
      <div style={{
        flex: 1,
        height: 6,
        backgroundColor: '#E2E8F0',
        borderRadius: 3,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${percent}%`,
          height: '100%',
          backgroundColor: THEME_COLORS.primary,
          borderRadius: 3,
          transition: 'width 300ms ease',
        }} />
      </div>
      <span style={{ fontSize: 11, minWidth: 32, textAlign: 'right' }}>{percent}%</span>
    </div>
  )
}

function renderHeatmap(value: number, min: number, max: number, isDarkMode: boolean): React.ReactNode {
  const bgColor = getHeatmapColor(value, min, max, isDarkMode)
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 4,
      backgroundColor: bgColor,
      color: isDarkMode ? '#FFF' : '#000',
      fontSize: 12,
      fontWeight: 500,
    }}>
      {value}
    </span>
  )
}

function renderSparkline(values: number[]): React.ReactNode {
  if (!values || values.length < 2) return null
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const width = 60
  const height = 20
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width
    const y = height - ((v - min) / range) * height
    return `${x},${y}`
  }).join(' ')

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline
        points={points}
        fill="none"
        stroke={THEME_COLORS.primary}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function renderNumeric(value: number | string, precision?: number): React.ReactNode {
  if (value === null || value === undefined || value === '') return null
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return String(value)
  const formatted = precision !== undefined ? num.toFixed(precision) : num.toLocaleString()
  return <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatted}</span>
}

function renderDate(value: string | Date): React.ReactNode {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) return String(value)
  return date.toLocaleDateString()
}

function renderDateTime(value: string | Date): React.ReactNode {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) return String(value)
  return date.toLocaleString()
}

function renderAvatar(value: string, name?: string): React.ReactNode {
  if (value) {
    return (
      <img
        src={value}
        alt={name || 'Avatar'}
        style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
      />
    )
  }
  const initials = name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?'
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
      borderRadius: '50%',
      backgroundColor: THEME_COLORS.primary,
      color: '#FFF',
      fontSize: 11,
      fontWeight: 600,
    }}>
      {initials}
    </span>
  )
}

export function buildColumns(columnsDef: ColumnDef[], isDarkMode: boolean): TanStackColumnDef<any, any>[] {
  const indexColumn: TanStackColumnDef<any, any> = {
    id: '_selectIndex',
    header: '#',
    size: COLUMN_CONFIG.INDEX_WIDTH,
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => row.index + 1,
  }

  const dataColumns: TanStackColumnDef<any, any>[] = columnsDef
    .filter(col => !col.hidden)
    .map(col => ({
      id: col.accessorKey,
      accessorKey: col.accessorKey,
      header: col.header,
      size: col.width || COLUMN_CONFIG.DEFAULT_WIDTH,
      minSize: col.minWidth || COLUMN_CONFIG.MIN_WIDTH,
      maxSize: col.maxWidth || COLUMN_CONFIG.MAX_WIDTH,
      enableSorting: col.sortable !== false,
      enableColumnFilter: col.filterable !== false,
      filterFn: (row: any, columnId: string, filterValue: any) => {
        if (!filterValue) return true
        const value = row.getValue(columnId)
        if (value === null || value === undefined) return false

        // Range filter for numeric columns
        if (typeof filterValue === 'object' && ('min' in filterValue || 'max' in filterValue)) {
          const numValue = typeof value === 'number' ? value : parseFloat(value)
          if (isNaN(numValue)) return false
          if (filterValue.min !== undefined && numValue < filterValue.min) return false
          if (filterValue.max !== undefined && numValue > filterValue.max) return false
          return true
        }

        // String filter
        const strValue = String(value).toLowerCase()
        return strValue.includes(String(filterValue).toLowerCase())
      },
      meta: {
        type: col.type,
        editable: col.editable,
        editType: col.editType,
        options: col.options,
        allowCreate: col.allowCreate,
        onCreateOption: col.onCreateOption,
        isNumeric: col.isNumeric || col.type === 'numeric',
        textAlign: col.textAlign,
        badgeColors: col.badgeColors,
      },
      cell: ({ getValue, row }) => {
        const value = getValue()

        if (col.render) {
          return col.render(value, row.original)
        }

        switch (col.type) {
          case 'country':
            return renderCountry(value as string)
          case 'badge':
            return renderBadge(value as string, isDarkMode, col.badgeColors)
          case 'link':
            return renderLink(value as string)
          case 'email':
            return renderEmail(value as string)
          case 'phone':
            return renderPhone(value as string)
          case 'boolean':
            return renderBoolean(value as boolean)
          case 'rating':
            return renderRating(value as number)
          case 'progress':
            return renderProgress(value as number)
          case 'heatmap':
            return renderHeatmap(value as number, col.min || 0, col.max || 100, isDarkMode)
          case 'sparkline':
            return renderSparkline(value as number[])
          case 'numeric':
            return renderNumeric(value as number, col.precision)
          case 'date':
            return renderDate(value as string)
          case 'datetime':
            return renderDateTime(value as string)
          case 'avatar':
            return renderAvatar(value as string, row.original.name)
          default:
            return value != null ? String(value) : ''
        }
      },
    }))

  return [indexColumn, ...dataColumns]
}
